import { computed } from 'vue';
import {
  between, maxLength, minLength, requiredIf, integer,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { TypeCampaignEnum } from '@/types';
import { includesValuesValidator, requiredWithMessage, segmentationCompleteValidator } from '@/helpers/CustomValidator';
import type {
  FlowOperatorMetadata, ValidationResult, Period, WarningValidator, WarningValidationContext, NodeWarning,
} from '@/types/flow-types';
import {
  isOnPeriodicChain,
  validateSegmentationHasDateConditions,
} from '@/composables/segmentations/segmentationValidation';
import Form from './Form.vue';

export interface ContactSegmentationData {
  custom_title: string;
  custom_description: string;
  outputs_number: number;
  grant_additional_delay: string;
  extra_time: Period;
  segmentation_config: any;
  initial_number_outputs?: number | null;
  is_persistent: string;
}

export const createDefaultData = (overrides: Partial<ContactSegmentationData> = {}): ContactSegmentationData => ({
  custom_title: '',
  custom_description: '',
  outputs_number: 1,
  grant_additional_delay: '0',
  extra_time: {
    value: 1,
    unit: 'DAY',
  },
  segmentation_config: [],
  is_persistent: '0',
  ...overrides,
});

/**
 * A boxcontactseg filter must include an exact-date condition when its branch
 * inherits an unbounded contact volume from a periodic launching trigger.
 * The unbounded chain propagates through successive boxcontactseg NO branches,
 * so the check is recursive (handled by isOnPeriodicChain).
 */
const periodicDateConditionValidator: WarningValidator<ContactSegmentationData> = {
  id: 'segmentation-periodic-date-condition',
  validate: (data: ContactSegmentationData, context: WarningValidationContext): NodeWarning | null => {
    if (!isOnPeriodicChain(context.operatorId, context.flow)) {
      return null;
    }

    const result = validateSegmentationHasDateConditions(data.segmentation_config);
    if (result.valid) {
      return null;
    }

    return {
      id: 'segmentation-periodic-date-condition',
      severity: 'error',
      location: 'box',
      message: result.message ?? 'automatedScenarios.validation.segmentation.missingDateCondition',
      messageParams: result.messageParams,
      blocking: true,
      showInForm: true,
    };
  },
};

const ContactSegmentation: FlowOperatorMetadata<ContactSegmentationData> = {
  id: 'boxcontactseg',
  kind: 'filter',
  label: 'flows.operators.contactSegmentation.label',
  icon: 'fa-filter',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
  incompatibilities: {},
  validators: [periodicDateConditionValidator],
  Form,
  defaultData: createDefaultData,

  outputs(data: ContactSegmentationData): number {
    return data.outputs_number ?? 1;
  },

  getDisplayLabel(data: ContactSegmentationData): string | null {
    return data.custom_title || null;
  },

  getSubtitle(data: ContactSegmentationData): string | null {
    return data.custom_description || null;
  },

  beforeInsert(data: ContactSegmentationData): ContactSegmentationData {
    const cleanData = { ...data } as any;
    if ('initial_number_outputs' in cleanData) {
      delete cleanData.initial_number_outputs;
    }

    if (data.is_persistent === '1') {
      cleanData.customPersistance = {
        boxcontactseg: ['{CURRENT_OPERATOR_ID}'],
      };
    } else {
      // Explicitly clear it rather than deleting the key: on edit, the editor
      // merges these values into the saved ones and only drops a key set to
      // undefined. A missing key would keep the previous value, and the "P"
      // persistence badge would stay after unticking "Persistent filter".
      cleanData.customPersistance = undefined;
    }

    return cleanData;
  },

  async validate(data: ContactSegmentationData): Promise<ValidationResult> {
    // Normalize outputs_number to number
    const normalizedData = {
      ...data,
      outputs_number: Number(data.outputs_number),
    };

    const rules = computed(() => ({
      custom_title: {
        requiredWithMessage: requiredWithMessage(),
        minLength: minLength(3),
        maxLength: maxLength(45),
      },
      outputs_number: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator([1, 2]),
      },
      grant_additional_delay: {
        requiredWithMessage: requiredWithMessage(),
        includesValuesValidator: includesValuesValidator(['0', '1']),
      },
      extra_time: {
        value: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          integer,
        },
        unit: {
          required: requiredIf(() => data.grant_additional_delay === '1'),
          includesValuesValidator: includesValuesValidator(['SECOND', 'MINUTE', 'HOUR', 'DAY']),
        },
      },
      segmentation_config: {
        requiredWithMessage: requiredWithMessage(),
        segmentationCompleteValidator: segmentationCompleteValidator(),
      },
    }));

    const v$ = useVuelidate(rules, normalizedData);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },
};

export default ContactSegmentation;
